import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ManageStagesLink } from '@/components/shared/manage-links'
import { useLifecycleStages } from '@/hooks/use-lifecycle-stages'
import { toast } from 'sonner'
import { Search } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Checkbox, ScrollArea, Separator } from '@/components/ui/misc'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Loading } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import { currentLocale } from '@/lib/i18n'
import {
  LIFECYCLE_STAGES,
  STAGE_LABELS,
  useContacts,
  type ContactListItem,
} from '@/hooks/use-contacts'
import {
  useOaAccounts,
  useZnsTemplates,
  useCreateZnsCampaign,
  usePreviewZnsCampaign,
  useTestSendZns,
  type ZnsTemplate,
} from '@/hooks/use-zns'
import { useSegments } from '@/hooks/use-cdp'

const ALL = '__all__'
const CONTACT_LIMIT = 50
const NO_SEGMENT = '__none__'
const NO_WINDOW = '__none__'
const HOURS = Array.from({ length: 24 }, (_, i) => i)
const fmtVnd = (n: number) => `${Math.round(n).toLocaleString(currentLocale())}đ`

export function CreateCampaignDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('marketing')
  const lifecycleStages = useLifecycleStages()
  const [name, setName] = useState('')
  const [accountId, setAccountId] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [templateData, setTemplateData] = useState<Record<string, string>>({})
  const [scheduledAt, setScheduledAt] = useState('')
  const [segmentId, setSegmentId] = useState(NO_SEGMENT)
  const [winStart, setWinStart] = useState(NO_WINDOW)
  const [winEnd, setWinEnd] = useState(NO_WINDOW)
  const [testPhone, setTestPhone] = useState('')

  // Chọn người nhận
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [stage, setStage] = useState<string>(ALL)
  const [selected, setSelected] = useState<Map<string, string>>(new Map())

  const accountsQuery = useOaAccounts()
  const templatesQuery = useZnsTemplates(accountId || undefined)
  const createMutation = useCreateZnsCampaign()
  const segmentsQuery = useSegments()
  const previewMutation = usePreviewZnsCampaign()
  const testMutation = useTestSendZns()

  // Reset khi đóng dialog
  useEffect(() => {
    if (!open) {
      setName('')
      setAccountId('')
      setTemplateId('')
      setTemplateData({})
      setScheduledAt('')
      setSegmentId(NO_SEGMENT)
      setWinStart(NO_WINDOW)
      setWinEnd(NO_WINDOW)
      setTestPhone('')
      previewMutation.reset()
      setSearchInput('')
      setSearch('')
      setStage(ALL)
      setSelected(new Map())
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Debounce tìm kiếm contact
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400)
    return () => clearTimeout(t)
  }, [searchInput])

  const contactParams = useMemo(
    () => ({
      page: 1,
      limit: CONTACT_LIMIT,
      search: search || undefined,
      lifecycleStage: stage === ALL ? undefined : stage,
    }),
    [search, stage],
  )
  const contactsQuery = useContacts(contactParams)

  const selectedTemplate: ZnsTemplate | undefined = useMemo(
    () => templatesQuery.data?.find((t) => t.templateId === templateId),
    [templatesQuery.data, templateId],
  )

  // Khi đổi tài khoản OA → reset mẫu + tham số
  function handleAccountChange(v: string) {
    setAccountId(v)
    setTemplateId('')
    setTemplateData({})
  }

  // Khi đổi mẫu → khởi tạo map tham số rỗng
  function handleTemplateChange(v: string) {
    setTemplateId(v)
    const tmpl = templatesQuery.data?.find((t) => t.templateId === v)
    const next: Record<string, string> = {}
    for (const p of tmpl?.params ?? []) next[p.name] = ''
    setTemplateData(next)
  }

  function toggleContact(c: ContactListItem) {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(c.id)) next.delete(c.id)
      else next.set(c.id, c.crmName || c.fullName || c.phone || t('znsCreate.noName'))
      return next
    })
  }

  function toggleAllOnPage(rows: ContactListItem[], checked: boolean) {
    setSelected((prev) => {
      const next = new Map(prev)
      for (const c of rows) {
        if (checked) next.set(c.id, c.crmName || c.fullName || c.phone || t('znsCreate.noName'))
        else next.delete(c.id)
      }
      return next
    })
  }

  const rows = contactsQuery.data?.contacts ?? []
  const allOnPageSelected = rows.length > 0 && rows.every((c) => selected.has(c.id))

  const canSubmit =
    !!name.trim() &&
    !!accountId &&
    !!templateId &&
    (selected.size > 0 || segmentId !== NO_SEGMENT) &&
    (winStart === NO_WINDOW) === (winEnd === NO_WINDOW) &&
    !createMutation.isPending

  async function handlePreview() {
    try {
      await previewMutation.mutateAsync({
        channelAccountId: accountId,
        templateId,
        contactIds: Array.from(selected.keys()),
        segmentId: segmentId === NO_SEGMENT ? null : segmentId,
      })
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  async function handleTestSend() {
    try {
      await testMutation.mutateAsync({ channelAccountId: accountId, templateId, templateData, phone: testPhone })
      toast.success(t('znsCreate.testSentToast'))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  const preview = previewMutation.data

  async function handleSubmit() {
    // Kiểm tra tham số bắt buộc
    const missing = (selectedTemplate?.params ?? []).filter(
      (p) => p.require && !templateData[p.name]?.trim(),
    )
    if (missing.length > 0) {
      toast.error(t('znsCreate.missingParamsToast', { params: missing.map((m) => m.name).join(', ') }))
      return
    }

    try {
      await createMutation.mutateAsync({
        name: name.trim(),
        channelAccountId: accountId,
        templateId,
        templateData,
        contactIds: Array.from(selected.keys()),
        segmentId: segmentId === NO_SEGMENT ? null : segmentId,
        windowStart: winStart === NO_WINDOW ? null : Number(winStart),
        windowEnd: winEnd === NO_WINDOW ? null : Number(winEnd),
        scheduledAt: scheduledAt ? new Date(scheduledAt).toISOString() : null,
      })
      toast.success(scheduledAt ? t('znsCreate.scheduledToast') : t('znsCreate.draftCreatedToast'))
      onOpenChange(false)
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('znsCreate.title')}</DialogTitle>
          <DialogDescription>{t('znsCreate.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Tên */}
          <div className="space-y-1.5">
            <Label htmlFor="zns-name">{t('znsCreate.nameLabel')}</Label>
            <Input
              id="zns-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('znsCreate.namePlaceholder')}
            />
          </div>

          {/* OA + Mẫu */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t('znsCreate.oaAccountLabel')}</Label>
              <Select value={accountId} onValueChange={handleAccountChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t('znsCreate.oaAccountPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {accountsQuery.data?.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.displayName || a.externalPageId || a.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>{t('znsCreate.templateLabel')}</Label>
              <Select
                value={templateId}
                onValueChange={handleTemplateChange}
                disabled={!accountId || templatesQuery.isLoading}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      !accountId
                        ? t('znsCreate.templateChooseAccountFirst')
                        : templatesQuery.isLoading
                          ? t('znsCreate.templateLoading')
                          : t('znsCreate.templatePlaceholder')
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {templatesQuery.data?.map((t) => (
                    <SelectItem key={t.templateId} value={t.templateId}>
                      {t.templateName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {accountId && !templatesQuery.isLoading && (templatesQuery.data?.length ?? 0) === 0 && (
            <p className="text-xs text-muted-foreground">
              {t('znsCreate.noTemplates')}
            </p>
          )}

          {/* Tham số mẫu */}
          {selectedTemplate && selectedTemplate.params.length > 0 && (
            <div className="space-y-2 rounded-lg border p-3">
              <p className="text-sm font-medium">{t('znsCreate.paramsTitle')}</p>
              <p className="text-xs text-muted-foreground">
                {t('znsCreate.paramsHint')}{' '}
                <code className="rounded bg-muted px-1">{'{{contact.fullName}}'}</code>,{' '}
                <code className="rounded bg-muted px-1">{'{{contact.phone}}'}</code>.
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {selectedTemplate.params.map((p) => (
                  <div key={p.name} className="space-y-1">
                    <Label className="flex items-center gap-1 text-xs">
                      {p.name}
                      {p.require && <span className="text-destructive">*</span>}
                      {p.type && (
                        <span className="font-normal text-muted-foreground">({p.type})</span>
                      )}
                    </Label>
                    <Input
                      value={templateData[p.name] ?? ''}
                      onChange={(e) =>
                        setTemplateData((prev) => ({ ...prev, [p.name]: e.target.value }))
                      }
                      placeholder={t('znsCreate.paramValuePlaceholder', { param: p.name })}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Lên lịch + khung giờ */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-1.5 sm:col-span-1">
              <Label htmlFor="zns-schedule">{t('znsCreate.scheduleLabel')}</Label>
              <Input
                id="zns-schedule"
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
              />
            </div>
            {[
              { label: t('znsCreate.windowFrom'), v: winStart, set: setWinStart },
              { label: t('znsCreate.windowTo'), v: winEnd, set: setWinEnd },
            ].map((f) => (
              <div key={f.label} className="space-y-1.5">
                <Label>{f.label}</Label>
                <Select value={f.v} onValueChange={f.set}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_WINDOW}>{t('znsCreate.windowNone')}</SelectItem>
                    {HOURS.map((h) => (
                      <SelectItem key={h} value={String(h)}>{h}:00</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            {t('znsCreate.windowHint')}
          </p>

          <Separator />

          {/* Chọn người nhận */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{t('znsCreate.recipientsLabel')}</Label>
              <Badge variant={selected.size > 0 ? 'default' : 'secondary'}>
                {t('znsCreate.selectedCount', { count: selected.size })}
              </Badge>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">{t('znsCreate.segmentHint')}</Label>
              <Select value={segmentId} onValueChange={setSegmentId}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_SEGMENT}>{t('znsCreate.noSegment')}</SelectItem>
                  {segmentsQuery.data?.segments.map((sg) => (
                    <SelectItem key={sg.id} value={sg.id}>
                      {sg.name} ({sg.contactCount})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-[180px] flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder={t('znsCreate.searchPlaceholder')}
                  className="pl-9"
                />
              </div>
              <Select value={stage} onValueChange={setStage}>
                <SelectTrigger className="w-[170px]">
                  <SelectValue placeholder={t('znsCreate.stagePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t('znsCreate.allStages')}</SelectItem>
                  {lifecycleStages.stages.map((s) => (
                    <SelectItem key={s.key} value={s.key}>
                      {s.label}
                    </SelectItem>
                  ))}
                <ManageStagesLink /></SelectContent>
              </Select>
            </div>

            <div className="rounded-lg border">
              <div className="flex items-center gap-2 border-b px-3 py-2">
                <Checkbox
                  checked={allOnPageSelected}
                  onCheckedChange={(v) => toggleAllOnPage(rows, v === true)}
                  disabled={rows.length === 0}
                />
                <span className="text-xs text-muted-foreground">
                  {t('znsCreate.selectAllVisible', { count: rows.length })}
                </span>
              </div>
              <ScrollArea className="h-52">
                {contactsQuery.isLoading ? (
                  <Loading label={t('znsCreate.loadingContacts')} />
                ) : rows.length === 0 ? (
                  <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                    {t('znsCreate.noContactsFound')}
                  </p>
                ) : (
                  <ul className="divide-y">
                    {rows.map((c) => (
                      <li
                        key={c.id}
                        className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-muted/40"
                        onClick={() => toggleContact(c)}
                      >
                        <Checkbox
                          checked={selected.has(c.id)}
                          onCheckedChange={() => toggleContact(c)}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">
                            {c.crmName || c.fullName || t('znsCreate.noName')}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            {c.phone || t('znsCreate.noPhone')}
                          </p>
                        </div>
                        {!c.phone && (
                          <Badge variant="warning" className="shrink-0">
                            {t('znsCreate.missingPhone')}
                          </Badge>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </ScrollArea>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('znsCreate.invalidPhoneHint')}
            </p>
          </div>

          {/* Xem trước + gửi thử */}
          <Separator />
          <div className="space-y-3 rounded-lg border p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreview}
                disabled={!accountId || !templateId || (selected.size === 0 && segmentId === NO_SEGMENT) || previewMutation.isPending}
              >
                {previewMutation.isPending ? t('znsCreate.calculating') : t('znsCreate.previewButton')}
              </Button>
              <Input
                className="h-8 w-40"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder={t('znsCreate.testPhonePlaceholder')}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleTestSend}
                disabled={!accountId || !templateId || !testPhone.trim() || testMutation.isPending}
              >
                {t('znsCreate.testSendButton')}
              </Button>
            </div>
            {preview && (
              <ul className="space-y-1 text-sm">
                <li>{t('znsCreate.previewWillSend')} <b>{preview.sendable}</b> / {preview.total} ({t('znsCreate.previewSkipped', { count: preview.skipped })})</li>
                {preview.estimatedCost != null && (
                  <li>{t('znsCreate.previewEstimatedCost')} <b>{fmtVnd(preview.estimatedCost)}</b> ({fmtVnd(preview.unitPrice ?? 0)}{t('znsCreate.perMessage')})</li>
                )}
                {preview.quotaRemaining != null && (
                  <li className={preview.exceedsQuota ? 'text-destructive' : ''}>
                    {t('znsCreate.previewQuotaRemaining')} <b>{preview.quotaRemaining}</b>
                    {preview.exceedsQuota && t('znsCreate.previewExceedsQuota')}
                  </li>
                )}
                {preview.templateStatus && preview.templateStatus !== 'ENABLE' && (
                  <li className="text-destructive">{t('znsCreate.previewTemplateNotUsable', { status: preview.templateStatus })}</li>
                )}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('znsCreate.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {createMutation.isPending ? t('znsCreate.creating') : t('znsCreate.createButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
